import { useState, FormEvent } from 'react';
import { useNavigate } from 'react-router-dom';
import { Mail, ArrowLeft } from 'lucide-react';
import { Input } from '../../common/components/inputs';
import { Button } from '../../common/components/buttons';
import { AuthCard } from '../ui';
import { useFormValidation } from '../hooks';
import { requestPasswordReset } from '../../../services/authService';

/**
 * ForgotPasswordPage - Página de recuperação de senha
 */
export const ForgotPasswordPage = () => {
  const navigate = useNavigate();
  const { validateEmail } = useFormValidation();

  // Form state
  const [email, setEmail] = useState('');

  // UI state
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [isLoading, setIsLoading] = useState(false);
  const [generalError, setGeneralError] = useState('');
  const [success, setSuccess] = useState(false);

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setGeneralError('');
    setErrors({});
    setSuccess(false);

    // Validação
    const emailError = validateEmail(email);
    if (emailError) {
      setErrors({ email: emailError });
      return;
    }

    // Submit
    setIsLoading(true);
    try {
      await requestPasswordReset(email);
      setSuccess(true);
      setTimeout(() => {
        navigate('/');
      }, 3000);
    } catch (err) {
      setGeneralError(err instanceof Error ? err.message : 'Falha ao solicitar redefinição de senha');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#1a1a1a] flex items-center justify-center p-6">
      <div className="w-full max-w-md">
        <button
          onClick={() => navigate('/')}
          className="flex items-center gap-2 text-[#CCCCCC] hover:text-[#F4CC04] transition-colors mb-6"
        >
          <ArrowLeft className="w-5 h-5" />
          <span>Voltar para login</span>
        </button>

        <AuthCard title="Recuperar senha" subtitle="Enviaremos um e-mail com instruções para redefinir sua senha" showTabs={false}>
          {success ? (
            <div className="space-y-4">
              <div className="bg-[rgba(76,175,80,0.12)] border border-[#4CAF50] rounded-lg p-6 text-center">
                <div className="w-16 h-16 bg-[#4CAF50] rounded-full flex items-center justify-center mx-auto mb-4">
                  <span className="text-3xl">✓</span>
                </div>
                <div className="text-[#4CAF50] font-semibold text-lg mb-2">
                  E-mail enviado!
                </div>
                <div className="text-[#CCCCCC] text-sm">
                  Uma nova senha temporária foi enviada para <strong>{email}</strong>.
                  Verifique sua caixa de entrada e spam.
                </div>
              </div>
              <div className="text-center text-[#888888] text-sm">
                Redirecionando para o login...
              </div>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="space-y-5">
              <Input
                label="E-mail"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                error={errors.email}
                icon={Mail}
                required
              />

              {generalError && (
                <div className="bg-[rgba(229,57,53,0.12)] border border-[#e53935] rounded-lg p-4">
                  <p className="text-[#e53935] text-sm">{generalError}</p>
                </div>
              )}

              <Button type="submit" variant="primary" fullWidth loading={isLoading}>
                Enviar instruções
              </Button>

              <p className="text-center text-[#CCCCCC] text-sm">
                Lembrou sua senha?{' '}
                <button
                  type="button"
                  onClick={() => navigate('/')}
                  className="text-[#F4CC04] hover:underline font-medium"
                >
                  Faça login
                </button>
              </p>
            </form>
          )}
        </AuthCard>
      </div>
    </div>
  );
};
